import { bus } from '@/core/EventBus';
import type { Player } from '@/gameplay/Player';
import type { Enemy } from '@/gameplay/Enemy';
import type { ChaseSystem } from '@/gameplay/Chase';
import { renderMinimapSvg, type NavigationSnapshot } from '@/ui/Minimap';

export interface HudOptions {
  readonly player: Player;
  readonly enemies: readonly Enemy[];
  readonly initialObjective?: string;
  readonly districtName?: () => string;
  readonly objectiveDistance?: () => number | null;
  readonly navigation?: () => NavigationSnapshot;
  readonly onRestartFromCheckpoint?: () => void;
  readonly chase?: ChaseSystem;
}

interface Notice {
  readonly element: HTMLDivElement;
  life: number;
}

const NOTICE_LIFETIME = 1.35;

export class Hud {
  readonly #root: HTMLDivElement;
  readonly #objective: HTMLDivElement;
  readonly #location: HTMLDivElement;
  readonly #districtBanner: HTMLDivElement;
  readonly #minimap: HTMLDivElement;
  readonly #healthFill: HTMLDivElement;
  readonly #healthText: HTMLDivElement;
  readonly #ammoText: HTMLDivElement;
  readonly #enemyText: HTMLDivElement;
  readonly #noticeLayer: HTMLDivElement;
  readonly #deathOverlay: HTMLDivElement;
  readonly #restartButton: HTMLButtonElement;
  readonly #notices: Notice[] = [];
  readonly #unsubscribers: Array<() => void> = [];
  #lastDistrict = '';
  #districtBannerLife = 0;

  constructor(
    container: HTMLElement,
    private readonly options: HudOptions,
  ) {
    this.#root = document.createElement('div');
    this.#root.className = 'na-hud';
    this.#root.innerHTML = `
      <section class="na-hud-objective" aria-label="Current objective">
        <div class="na-hud-label">Objective</div>
        <div class="na-hud-objective-text"></div>
        <div class="na-hud-location"></div>
      </section>
      <section class="na-hud-minimap" aria-label="Minimap">
      </section>
      <section class="na-hud-district" aria-live="polite"></section>
      <section class="na-hud-status" aria-label="Player status">
        <div class="na-hud-health">
          <div class="na-hud-health-fill"></div>
        </div>
        <div class="na-hud-health-text"></div>
        <div class="na-hud-ammo"></div>
      </section>
      <section class="na-hud-threats" aria-label="Threat status"></section>
      <!-- Helderheid en geluid staan in het instellingenpaneel (ui/settings.ts), zodat alle
           beeldinstellingen bij elkaar zitten in plaats van los over de HUD verspreid. -->
      <section class="na-hud-notices" aria-live="polite"></section>
      <section class="na-death-overlay" aria-live="assertive" aria-hidden="true">
        <div class="na-death-drips" aria-hidden="true">
          ${Array.from({ length: 18 }, (_, index) => `<span style="${deathDripStyle(index)}"></span>`).join('')}
        </div>
        <div class="na-death-copy">
          <div class="na-death-title">Je bent dood</div>
          <button class="na-death-restart" type="button">Start vanaf checkpoint</button>
        </div>
      </section>
    `;
    installHudStyles(container.ownerDocument);
    container.appendChild(this.#root);

    this.#objective = this.#query('.na-hud-objective-text');
    this.#location = this.#query('.na-hud-location');
    this.#districtBanner = this.#query('.na-hud-district');
    this.#minimap = this.#query('.na-hud-minimap');
    this.#healthFill = this.#query('.na-hud-health-fill');
    this.#healthText = this.#query('.na-hud-health-text');
    this.#ammoText = this.#query('.na-hud-ammo');
    this.#enemyText = this.#query('.na-hud-threats');
    this.#noticeLayer = this.#query('.na-hud-notices');
    this.#deathOverlay = this.#query('.na-death-overlay');
    this.#restartButton = this.#query('.na-death-restart');
    this.setObjective(options.initialObjective ?? 'Find the loyal soldiers');
    this.#restartButton.addEventListener('click', () => {
      options.onRestartFromCheckpoint?.();
      this.hideDeathOverlay();
    });
    this.#wireEvents();
    this.update(0);
  }

  setObjective(text: string): void {
    this.#objective.textContent = text;
  }

  hideDeathOverlay(): void {
    this.#deathOverlay.classList.remove('is-visible');
    this.#deathOverlay.setAttribute('aria-hidden', 'true');
  }

  update(dt: number): void {
    this.#updatePlayerStatus();
    this.#updateLocation();
    this.#updateNavigation(dt);
    this.#updateEnemies();
    this.#updateChase();
    this.#updateNotices(dt);
  }

  dispose(): void {
    for (const unsubscribe of this.#unsubscribers) unsubscribe();
    this.#unsubscribers.length = 0;
    this.#root.remove();
  }

  #wireEvents(): void {
    this.#unsubscribers.push(
      bus.on('mission:objectiveChanged', ({ text }) => this.setObjective(text)),
      bus.on('player:damaged', ({ amount }) => this.#pushNotice(`Hit -${Math.round(amount)}`, 'bad')),
      bus.on('player:died', () => {
        this.#pushNotice('Luca down', 'bad');
        this.#showDeathOverlay();
      }),
      bus.on('enemy:killed', ({ id }) => this.#pushNotice(`Enemy ${id} down`, 'good')),
      bus.on('enemy:alerted', () => this.#pushNotice('Spotted', 'bad')),
      bus.on('mission:completed', () => this.#pushNotice('Objective complete', 'good')),
      bus.on('mission:failed', ({ reason }) => this.#pushNotice(reason, 'bad')),
      bus.on('safehouse:entered', ({ name }) => this.#pushNotice(`${name} entered`, 'good')),
      bus.on('vehicle:entered', () => this.#pushNotice('Entered car', 'good')),
      bus.on('vehicle:exited', () => this.#pushNotice('Exited car', 'good')),
      bus.on('vehicle:burned', () => this.#pushNotice('Vehicle burned', 'bad')),
      bus.on('vehicle:stolen', () => this.#pushNotice('Car stolen', 'good')),
      bus.on('chase:started', () => this.#pushNotice('Chase started', 'bad')),
      bus.on('chase:escaped', () => this.#pushNotice('Escaped', 'good')),
      bus.on('chase:failed', ({ reason }) => this.#pushNotice(reason, 'bad')),
    );
  }

  #updatePlayerStatus(): void {
    const { player } = this.options;
    const maxHealth = 100;
    const health = Math.max(0, Math.min(maxHealth, player.health));
    this.#healthFill.style.width = `${health}%`;
    this.#healthText.textContent = `Health ${Math.round(health)}`;

    const { weapon } = player;
    const reload = weapon.reloading ? ' Reloading' : '';
    this.#ammoText.textContent = `${weapon.spec.name} ${weapon.ammo}/${weapon.spec.magazine}${reload}`;
    this.#root.classList.toggle('is-damaged', health <= 35 && health > 0);
    this.#root.classList.toggle('is-dead', health === 0);
  }

  #updateLocation(): void {
    const district = this.options.districtName?.() ?? '';
    const distance = this.options.objectiveDistance?.();
    const distanceText = typeof distance === 'number' ? ` · ${Math.round(distance)}m` : '';
    this.#location.textContent = district ? `${district}${distanceText}` : distanceText.replace(' · ', '');
  }

  #updateNavigation(dt: number): void {
    const snapshot = this.options.navigation?.();
    if (!snapshot) return;

    if (snapshot.district !== this.#lastDistrict) {
      this.#lastDistrict = snapshot.district;
      this.#districtBanner.textContent = snapshot.district;
      this.#districtBannerLife = 1.8;
    }

    this.#districtBannerLife = Math.max(0, this.#districtBannerLife - dt);
    this.#districtBanner.classList.toggle('is-visible', this.#districtBannerLife > 0);

    this.#minimap.innerHTML = renderMinimapSvg(snapshot);
  }

  #updateEnemies(): void {
    const alive = this.options.enemies.filter((enemy) => enemy.alive).length;
    const alerted = this.options.enemies.filter(
      (enemy) => enemy.state === 'alert' || enemy.state === 'chase' || enemy.state === 'shoot',
    ).length;
    this.#enemyText.textContent = alerted > 0 ? `Enemies ${alive}  Alert ${alerted}` : `Enemies ${alive}`;
  }

  #updateChase(): void {
    const chase = this.options.chase;
    if (!chase || chase.level === 'none') return;
    const snapshot = chase.snapshot();
    if (snapshot.level === 'escaping') {
      this.#enemyText.textContent = `Chase escape ${Math.round(snapshot.escapeProgress * 100)}%`;
      return;
    }
    this.#enemyText.textContent = snapshot.level === 'searching'
      ? 'Chase searching'
      : `Chase active ${snapshot.visibleThreats + snapshot.nearbyThreats}`;
  }

  #pushNotice(text: string, tone: 'good' | 'bad'): void {
    const element = document.createElement('div');
    element.className = `na-hud-notice is-${tone}`;
    element.textContent = text;
    this.#noticeLayer.appendChild(element);
    this.#notices.push({ element, life: NOTICE_LIFETIME });
  }

  #showDeathOverlay(): void {
    this.#deathOverlay.classList.add('is-visible');
    this.#deathOverlay.setAttribute('aria-hidden', 'false');
    this.#restartButton.focus();
  }

  #updateNotices(dt: number): void {
    for (let i = this.#notices.length - 1; i >= 0; i--) {
      const notice = this.#notices[i]!;
      notice.life -= dt;
      const fade = Math.max(0, notice.life / NOTICE_LIFETIME);
      notice.element.style.opacity = String(Math.min(1, fade * 1.35));
      notice.element.style.transform = `translateY(${(1 - fade) * -10}px)`;
      if (notice.life <= 0) {
        notice.element.remove();
        this.#notices.splice(i, 1);
      }
    }
  }

  #query<T extends HTMLElement>(selector: string): T {
    const found = this.#root.querySelector<T>(selector);
    if (!found) throw new Error(`HUD element missing: ${selector}`);
    return found;
  }
}

function installHudStyles(document: Document): void {
  if (document.getElementById('na-hud-styles')) return;
  const style = document.createElement('style');
  style.id = 'na-hud-styles';
  style.textContent = `
    .na-hud {
      position: absolute;
      inset: 0;
      pointer-events: none;
      color: #e8ddc8;
      font-family: ui-monospace, "SF Mono", Menlo, monospace;
      text-shadow: 0 2px 6px rgba(0, 0, 0, 0.65);
    }
    .na-hud-objective {
      position: absolute;
      left: 18px;
      top: 18px;
      width: min(460px, calc(100vw - 36px));
      padding: 10px 12px;
      border-left: 3px solid #e0951c;
      background: rgba(8, 8, 10, 0.68);
    }
    .na-hud-label {
      color: #e0951c;
      font-size: 11px;
      line-height: 1;
      text-transform: uppercase;
    }
    .na-hud-objective-text {
      margin-top: 5px;
      font-size: 15px;
      line-height: 1.25;
    }
    .na-hud-location {
      min-height: 14px;
      margin-top: 6px;
      color: rgba(232, 221, 200, 0.72);
      font-size: 11px;
      line-height: 1.2;
      text-transform: uppercase;
    }
    .na-hud-minimap {
      position: absolute;
      left: 18px;
      top: 104px;
      width: 196px;
      height: 118px;
      overflow: hidden;
      border: 1px solid rgba(232, 221, 200, 0.22);
      background: rgba(8, 8, 10, 0.58);
    }
    .na-hud-map-svg {
      display: block;
      width: 100%;
      height: 100%;
      pointer-events: none;
    }
    .na-hud-map-bg {
      fill: rgba(8, 8, 10, 0.32);
    }
    .na-hud-map-chunk {
      fill: rgba(35, 37, 39, 0.48);
      stroke: rgba(232, 221, 200, 0.18);
      stroke-width: 0.8;
    }
    .na-hud-map-chunk.is-active {
      fill: rgba(58, 58, 56, 0.42);
      stroke: rgba(224, 149, 28, 0.42);
    }
    .na-hud-map-water {
      fill: rgba(29, 77, 82, 0.78);
    }
    .na-hud-map-road {
      fill: rgba(58, 58, 56, 0.92);
      stroke: rgba(232, 221, 200, 0.16);
      stroke-width: 0.7;
    }
    .na-hud-map-route {
      stroke: rgba(224, 149, 28, 0.82);
      stroke-width: 2;
      stroke-linecap: round;
      stroke-dasharray: 4 3;
    }
    .na-hud-map-label,
    .na-hud-map-distance {
      fill: rgba(232, 221, 200, 0.72);
      font-size: 8px;
      line-height: 1;
      text-transform: uppercase;
      paint-order: stroke;
      stroke: rgba(8, 8, 10, 0.88);
      stroke-width: 2;
    }
    .na-hud-map-safehouse-label,
    .na-hud-map-distance {
      fill: #e0951c;
      font-size: 7px;
      text-anchor: middle;
    }
    .na-hud-map-player {
      fill: #e8ddc8;
      stroke: rgba(8, 8, 10, 0.85);
      stroke-width: 1.2;
    }
    .na-hud-map-objective {
      fill: #e0951c;
      stroke: rgba(8, 8, 10, 0.85);
      stroke-width: 1.2;
    }
    .na-hud-map-heading {
      stroke: #e8ddc8;
      stroke-width: 1.8;
      stroke-linecap: round;
    }
    .na-hud-district {
      position: absolute;
      left: 50%;
      top: 18px;
      min-width: 180px;
      padding: 8px 12px;
      transform: translate(-50%, -12px);
      border-top: 1px solid rgba(224, 149, 28, 0.76);
      border-bottom: 1px solid rgba(224, 149, 28, 0.38);
      background: rgba(8, 8, 10, 0.62);
      color: #e8ddc8;
      font-size: 13px;
      line-height: 1;
      letter-spacing: 0;
      opacity: 0;
      text-align: center;
      text-transform: uppercase;
      transition: opacity 180ms ease, transform 180ms ease;
    }
    .na-hud-district.is-visible {
      opacity: 1;
      transform: translate(-50%, 0);
    }
    .na-hud-status {
      position: absolute;
      left: 18px;
      bottom: 18px;
      display: grid;
      grid-template-columns: 154px 86px;
      gap: 6px 10px;
      align-items: center;
      padding: 10px 12px;
      background: rgba(8, 8, 10, 0.68);
      border: 1px solid rgba(232, 221, 200, 0.22);
    }
    .na-hud-health {
      grid-column: 1 / -1;
      height: 8px;
      background: rgba(232, 221, 200, 0.18);
      overflow: hidden;
    }
    .na-hud-health-fill {
      height: 100%;
      width: 100%;
      background: #8f1d18;
      transition: width 120ms linear;
    }
    .na-hud-health-text,
    .na-hud-ammo,
    .na-hud-threats {
      font-size: 12px;
      line-height: 1.2;
      white-space: nowrap;
    }
    .na-hud-threats {
      position: absolute;
      right: 18px;
      bottom: 18px;
      padding: 8px 10px;
      background: rgba(8, 8, 10, 0.68);
      border: 1px solid rgba(232, 221, 200, 0.22);
    }
        .na-hud-notices {
      position: absolute;
      left: 50%;
      top: 22%;
      display: grid;
      gap: 6px;
      transform: translateX(-50%);
    }
    .na-hud-notice {
      min-width: 120px;
      padding: 7px 10px;
      text-align: center;
      background: rgba(8, 8, 10, 0.78);
      border: 1px solid rgba(232, 221, 200, 0.25);
      transition: opacity 80ms linear, transform 80ms linear;
    }
    .na-hud-notice.is-good {
      border-color: rgba(224, 149, 28, 0.78);
    }
    .na-hud-notice.is-bad {
      border-color: rgba(143, 29, 24, 0.9);
      color: #f1b3a5;
    }
    .na-hud.is-damaged .na-hud-health-fill {
      background: #e0951c;
    }
    .na-hud.is-dead .na-hud-health-fill {
      background: #272822;
    }
    .na-death-overlay {
      position: absolute;
      inset: 0;
      display: grid;
      place-items: center;
      overflow: hidden;
      pointer-events: none;
      opacity: 0;
      background:
        radial-gradient(circle at 50% 42%, rgba(143, 29, 24, 0.18), transparent 32%),
        rgba(8, 8, 10, 0.48);
      transition: opacity 260ms ease;
    }
    .na-death-overlay.is-visible {
      pointer-events: auto;
      opacity: 1;
    }
    .na-death-drips {
      position: absolute;
      inset: 0;
      pointer-events: none;
    }
    .na-death-drips span {
      position: absolute;
      top: -22vh;
      display: block;
      width: var(--drip-width);
      height: var(--drip-height);
      border-radius: 999px 999px 55% 55%;
      background: linear-gradient(180deg, rgba(143, 29, 24, 0.96), rgba(94, 31, 38, 0.72));
      box-shadow: 0 0 12px rgba(143, 29, 24, 0.36);
      animation: na-blood-drip var(--drip-duration) linear infinite;
      animation-delay: var(--drip-delay);
    }
    .na-death-copy {
      position: relative;
      display: grid;
      gap: 18px;
      justify-items: center;
      padding: 0 18px;
      text-align: center;
    }
    .na-death-title {
      color: #f1b3a5;
      font-size: clamp(42px, 8vw, 92px);
      line-height: 0.92;
      text-transform: uppercase;
      text-shadow: 0 5px 0 rgba(55, 10, 12, 0.95), 0 0 28px rgba(143, 29, 24, 0.55);
    }
    .na-death-restart {
      pointer-events: auto;
      min-width: 238px;
      min-height: 44px;
      padding: 11px 18px;
      border: 1px solid rgba(232, 221, 200, 0.72);
      background: #e8ddc8;
      color: #08080a;
      font: inherit;
      font-size: 13px;
      text-transform: uppercase;
      cursor: pointer;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.42);
    }
    .na-death-restart:hover,
    .na-death-restart:focus-visible {
      background: #e0951c;
      outline: 2px solid rgba(232, 221, 200, 0.72);
      outline-offset: 3px;
    }
    @keyframes na-blood-drip {
      0% {
        transform: translate3d(0, -20vh, 0) scaleY(0.78);
      }
      16% {
        transform: translate3d(0, 0, 0) scaleY(1.08);
      }
      100% {
        transform: translate3d(0, 125vh, 0) scaleY(1);
      }
    }
    @media (max-width: 640px) {
      .na-hud-objective {
        top: 10px;
        left: 10px;
        width: min(236px, calc(100vw - 148px));
        min-width: 0;
        padding: 8px 10px;
      }
      .na-hud-objective-text {
        font-size: 13px;
        line-height: 1.18;
      }
      .na-hud-minimap {
        top: 116px;
        left: 10px;
        width: min(196px, calc(100vw - 20px));
      }
      .na-hud-status {
        left: 10px;
        bottom: 10px;
        grid-template-columns: minmax(112px, 1fr) auto;
        width: min(252px, calc(100vw - 20px));
      }
      .na-hud-threats {
        right: 10px;
        bottom: 86px;
      }
      .na-death-restart {
        min-width: min(260px, calc(100vw - 44px));
      }
    }
    @media (max-width: 360px) {
      .na-hud-objective {
        width: calc(100vw - 20px);
        padding-right: 126px;
      }
      .na-hud-minimap {
        top: 132px;
      }
    }
  `;
  document.head.appendChild(style);
}

function deathDripStyle(index: number): string {
  const left = [3, 9, 16, 21, 28, 33, 41, 47, 54, 59, 65, 72, 78, 84, 89, 94, 37, 69][index]!;
  const width = 10 + ((index * 7) % 15);
  const height = 72 + ((index * 31) % 118);
  const duration = 4.6 + ((index * 13) % 23) / 10;
  const delay = -((index * 0.47) % 5.8);
  return `left:${left}vw;--drip-width:${width}px;--drip-height:${height}px;--drip-duration:${duration}s;--drip-delay:${delay}s`;
}
